import { userService } from "../user/service";
import { DbUser } from "../user/models";
import { axiosInstance } from "../rest/axios";
import { getCookie } from "cookies-next";
import { firebaseAuth, firebaseDb } from "../firebase/client-app";
import { collection, deleteDoc, doc, Firestore } from "firebase/firestore/lite";
import { getAuth, signOut, signInWithEmailAndPassword } from "firebase/auth";

export class AuthService {
  private userSessionRef(firestore: Firestore, userId: string) {
    return collection(firestore, "users/" + userId + "/user_sessions");
  }

  async sendOtp(phone: string, recaptchaToken: string): Promise<string> {
    const res = await axiosInstance.post("auth/otp/send/phone", {
      phone: phone,
      recaptcha_token: recaptchaToken,
    });
    return res.data["id"];
  }

  async sendOtpEmail(email: string, recaptchaToken: string): Promise<string> {
    const res = await axiosInstance.post("auth/otp/send/email", {
      email: email,
      recaptcha_token: recaptchaToken,
    });
    return res.data["id"];
  }

  async verifyOtp(id: string, phone: string, otp: string): Promise<string> {
    const res = await axiosInstance.post("auth/otp/verify", {
      id: id,
      phone: phone,
      otp: otp,
    });
    return res.data["custom_token"];
  }

  async verifyOtp2(
    id: string,
    otp: string,
    authMethod: "phone" | "email",
    phone?: string,
    email?: string,
  ): Promise<string> {
    const body: any = {
      id: id,
      otp: otp,
      auth_method: authMethod,
    };

    if (authMethod === "phone" && phone) {
      body.phone = phone;
    } else if (authMethod === "email" && email) {
      body.email = email;
    }

    const res = await axiosInstance.post("auth/otp/verify2", body);
    return res.data["custom_token"];
  }

  async loginWithPhone(userId: string, phone: string): Promise<DbUser> {
    const user = await userService.upsertUser(userId, phone);
    return user;
  }

  async loginWithEmail(userId: string, email: string): Promise<DbUser> {
    const user = await userService.upsertUserByEmail(userId, email);
    return user;
  }

  async updateUserProfile(user: DbUser): Promise<DbUser> {
    const updatedUser = await userService.updateUserProfile(user);
    return updatedUser;
  }

  async logoutUser(userId: string) {
    const sessionId = getCookie("session_id")?.toString();
    if (sessionId) {
      await this.clearUserSession(sessionId, userId);
    }
    await firebaseAuth.signOut();
  }

  async clearUserSession(
    sessionId: string,
    userId: string,
    firestore: Firestore = firebaseDb,
  ) {
    await deleteDoc(doc(this.userSessionRef(firestore, userId), sessionId));
  }

  async createAdmin(email: string, password: string, name: string) {
    const res = await axiosInstance.post("admin", {
      name: name,
      email: email,
      password: password,
    });

    return res.data;
  }

  async adminLogin(email: string, password: string) {
    const res = await axiosInstance.post("admin/login", {
      email: email,
      password: password,
    });

    const userCredential = await signInWithEmailAndPassword(
      firebaseAuth,
      email,
      password,
    );

    return {
      userData: res.data,
      userCredential: userCredential,
    };
  }

  async verifyAdminToken(token: string) {
    const res = await axiosInstance.post("admin/token/verify", {
      token: token,
    });

    return res.data;
  }
}

export const authService = new AuthService();
